import React, { useState, useEffect, useRef } from 'react';
import { PlayerProfile } from '../types';
import {
  getAllProfiles,
  resetAllRegularPlayerSaves,
  resetSinglePlayerSave,
  deleteSinglePlayerProfile,
  ADMIN_PLAYER_ID
} from '../utils/saveManager';
import { playSound } from '../audio';
import { BerberRugBorder } from './Sprites';
import { useKabyleTTS } from '../hooks/useKabyleTTS';
import {
  restoreRecordedAudiosBatch,
  syncLocalAudiosToFirestore,
  syncFromSourceServer
} from '../utils/audioStorage';
import {
  getTitleArtwork,
  saveTitleArtwork,
  deleteTitleArtwork,
  subscribeTitleArtwork,
  syncTitleArtworkFromServer
} from '../utils/titleArtworkStorage';
import {
  getWordOverridesMap,
  subscribeWordOverrides,
  resetWordOverride,
  importWordOverridesBatch,
  syncWordOverridesWithFirestore,
} from '../utils/wordOverridesStorage';
import {
  exportFullSystemBackup,
  importFullSystemBackup
} from '../utils/backupRestoreManager';
import { ALL_UNIQUE_WORDS, SORTED_CATEGORIES, LEVEL_DATA } from '../data/gameData';
import { DAILY_DIALOGUES_2026 } from '../data/dialoguesData';
import { SMS_DISCUSSIONS } from '../data/smsDiscussionsData';
import { getTalePageAudioKey, hasRecordedAudio } from '../utils/audioStorage';
import { ScrollDualScreenReader } from './ScrollDualScreenReader';
import { TTSButton } from './TTSButton';
import { MusicToggleButton } from './MusicToggleButton';
import { AddPublicWordModal } from './AddPublicWordModal';

interface AdminDashboardViewProps {
  onBack: () => void;
  onPlayerResetOrSwitch?: () => void;
  onStartLevel?: (levelIndex: number) => void;
}

export const AdminDashboardView: React.FC<AdminDashboardViewProps> = React.memo(({
  onBack,
  onPlayerResetOrSwitch,
  onStartLevel
}) => {
  const [profilesMap, setProfilesMap] = useState<Record<string, PlayerProfile>>(() => getAllProfiles());
  const [showConfirmResetAll, setShowConfirmResetAll] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [selectedPlayerToReset, setSelectedPlayerToReset] = useState<PlayerProfile | null>(null);
  const [selectedPlayerToDelete, setSelectedPlayerToDelete] = useState<PlayerProfile | null>(null);
  const [isAddWordModalOpen, setIsAddWordModalOpen] = useState(false);
  const [readingTaleScroll, setReadingTaleScroll] = useState<{
    title: string;
    content: string;
    sanctuaryName?: string;
  } | null>(null);
  const [contentStudioTab, setContentStudioTab] = useState<'contes' | 'dialogues' | 'sms'>('contes');

  // Title Screen Artwork Management
  const [serverArtworkUrl, setServerArtworkUrl] = useState<string | null>(() => getTitleArtwork());
  const [pendingArtworkDataUrl, setPendingArtworkDataUrl] = useState<string | null>(null);
  const [isUploadingArtwork, setIsUploadingArtwork] = useState(false);
  const [isDeletingArtwork, setIsDeletingArtwork] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Backup & Anti-Data-Loss System State
  const [syncStatusMsg, setSyncStatusMsg] = useState<string | null>(null);
  const [audioVersion, setAudioVersion] = useState(0);
  const backupFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleAudioUpdate = () => {
      setAudioVersion((v) => v + 1);
    };
    window.addEventListener('kabyle_audio_updated', handleAudioUpdate);
    return () => {
      window.removeEventListener('kabyle_audio_updated', handleAudioUpdate);
    };
  }, []);

  // Firebase Firestore Realtime Cloud Sync State
  const isPreEnv = typeof window !== 'undefined' && window.location.hostname.includes('ais-pre');
  const [isSyncingCloud, setIsSyncingCloud] = useState<boolean>(false);

  const handleCloudSyncNow = async () => {
    playSound('click');
    setIsSyncingCloud(true);
    setSyncStatusMsg('⏳ Synchronisation en direct avec Firebase Firestore en cours...');
    try {
      const res = await syncLocalAudiosToFirestore();
      const wordRes = await syncWordOverridesWithFirestore();
      await syncFromSourceServer();
      setAudioVersion((v) => v + 1);
      setServerArtworkUrl(getTitleArtwork());
      playSound('correct');
      const msg = `☁️ Synchronisation Cloud réussie ! (${res.pushed} voix, ${wordRes.pulled + wordRes.pushed} mots)`;
      setSyncStatusMsg(msg);
      triggerToast(msg);
    } catch (err: any) {
      playSound('wrong');
      const errMsg = `❌ Erreur de synchronisation cloud: ${err?.message || 'Erreur réseau'}`;
      setSyncStatusMsg(errMsg);
      triggerToast(errMsg);
    } finally {
      setIsSyncingCloud(false);
    }
  };

  useEffect(() => {
    const unsubscribe = subscribeTitleArtwork((url) => {
      setServerArtworkUrl(url);
    });
    return unsubscribe;
  }, []);

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      triggerToast('❌ Veuillez sélectionner un fichier image valide (JPG, PNG, WebP).');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const rawDataUrl = e.target?.result as string;
      
      // Optimisation dynamique pour Firestore : on maximise la qualité mais on DOIT 
      // rester sous la limite stricte de 1MB par document de la base de données.
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const MAX_DIM = 2560; // Évite les crashs de mémoire avec les images 4K+
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          
          let quality = 0.98;
          let finalDataUrl = canvas.toDataURL('image/webp', quality);
          
          // Limite Firestore = 1 048 576 bytes. Marge de sécurité à 950 000.
          while (finalDataUrl.length > 950000 && quality > 0.6) {
            quality -= 0.05;
            finalDataUrl = canvas.toDataURL('image/webp', quality);
          }
          
          setPendingArtworkDataUrl(finalDataUrl);
          playSound('click');
        } else {
          setPendingArtworkDataUrl(rawDataUrl);
          playSound('click');
        }
      };
      img.onerror = () => {
        setPendingArtworkDataUrl(rawDataUrl);
        playSound('click');
      };
      img.src = rawDataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleSaveArtworkToServer = async () => {
    if (!pendingArtworkDataUrl) return;
    setIsUploadingArtwork(true);
    playSound('stele');
    const result = await saveTitleArtwork(pendingArtworkDataUrl);
    setIsUploadingArtwork(false);
    if (result.success) {
      setPendingArtworkDataUrl(null);
      playSound('correct');
      triggerToast('✅ Artwork de l\'écran titre sauvegardé et appliqué sur le serveur avec succès !');
    } else {
      playSound('wrong');
      triggerToast(`❌ Erreur : ${result.error || 'Impossible d\'enregistrer l\'image.'}`);
    }
  };

  const handleDeleteArtwork = async () => {
    setIsDeletingArtwork(true);
    playSound('wrong');
    const ok = await deleteTitleArtwork();
    setIsDeletingArtwork(false);
    setPendingArtworkDataUrl(null);
    if (ok) {
      triggerToast('🗑️ Artwork personnalisé supprimé du serveur. Retour au fond épuré par défaut.');
    } else {
      triggerToast('❌ Erreur lors de la suppression de l\'artwork.');
    }
  };

  const handleExportBackup = async () => {
    playSound('click');
    const res = await exportFullSystemBackup();
    if (res.success) {
      playSound('correct');
      setSyncStatusMsg(`✅ Sauvegarde téléchargée : "${res.filename}". Conservez ce fichier précieusement !`);
    } else {
      playSound('wrong');
      setSyncStatusMsg(`❌ Erreur lors de l'exportation : ${res.error}`);
    }
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    playSound('click');
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const content = evt.target?.result as string;
      if (content) {
        setSyncStatusMsg('⏳ Restauration en cours...');
        const res = await importFullSystemBackup(content);
        if (res.success) {
          playSound('correct');
          setAudioVersion((v) => v + 1);
          setServerArtworkUrl(getTitleArtwork());
          setSyncStatusMsg(
            `✅ Restauration réussie ! (${res.recordedAudiosRestoredCount || 0} voix micro, ${res.wordsRestoredCount} mot(s) dictionnaire, artwork ${res.artworkRestored ? 'restauré' : 'inchangé'})`
          );
          triggerToast(`✅ ${res.recordedAudiosRestoredCount || 0} voix micro restaurées !`);
        } else {
          playSound('wrong');
          setSyncStatusMsg(`❌ Échec de la restauration : ${res.error}`);
          triggerToast('❌ Échec de la restauration');
        }
      }
      if (backupFileInputRef.current) backupFileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const profilesList: PlayerProfile[] = Object.values(profilesMap);
  const regularPlayers = profilesList.filter((p) => p.id !== ADMIN_PLAYER_ID && !p.isAdmin);
  const adminProfile = profilesMap[ADMIN_PLAYER_ID];

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const handleResetAllRegularPlayers = () => {
    playSound('correct');
    const result = resetAllRegularPlayerSaves();
    setProfilesMap(getAllProfiles());
    setShowConfirmResetAll(false);
    triggerToast(`✅ ${result.resetCount} sauvegarde(s) de joueur réinitialisée(s). Le joueur Admin reste intact et protégé !`);
    if (onPlayerResetOrSwitch) onPlayerResetOrSwitch();
  };

  const handleResetSingle = (player: PlayerProfile) => {
    if (player.id === ADMIN_PLAYER_ID) return;
    playSound('click');
    resetSinglePlayerSave(player.id);
    setProfilesMap(getAllProfiles());
    setSelectedPlayerToReset(null);
    triggerToast(`✅ Progression du joueur "${player.name}" réinitialisée.`);
    if (onPlayerResetOrSwitch) onPlayerResetOrSwitch();
  };

  const handleDeleteSingle = (player: PlayerProfile) => {
    if (player.id === ADMIN_PLAYER_ID) return;
    playSound('wrong');
    deleteSinglePlayerProfile(player.id);
    setProfilesMap(getAllProfiles());
    setSelectedPlayerToDelete(null);
    triggerToast(`🗑️ Profil du joueur "${player.name}" supprimé.`);
    if (onPlayerResetOrSwitch) onPlayerResetOrSwitch();
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'Non enregistrée';
    try {
      const d = new Date(timestamp);
      return d.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Récemment';
    }
  };

  return (
    <div className="fixed inset-0 w-full h-[100dvh] bg-[#090d16] text-slate-100 flex flex-col z-50 overflow-hidden select-none font-mono">
      {/* Top Rug Border */}
        <BerberRugBorder />

        {/* Header */}
        <header className="pt-4 pb-3 px-4 sm:px-6 bg-[#111827] border-b border-amber-500/40 shadow-lg flex flex-col gap-2 flex-shrink-0 z-30">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => {
                  playSound('click');
                  onBack();
                }}
                className="px-3 py-1.5 bg-red-900 hover:bg-red-800 text-white font-black text-xs uppercase tracking-wider border border-red-500 rounded-lg shadow-[1px_1px_0_red] active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
              >
                <svg className="w-3 h-3 fill-white shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="17,4 7,12 17,20" />
                </svg>
                <span className="text-white">Menu</span>
              </button>
            <div className="truncate">
              <h1 className="text-lg sm:text-xl font-black text-amber-400 uppercase tracking-tight flex items-center gap-2">
                <span>STUDIO & GESTION</span>
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <MusicToggleButton variant="badge" />
            
          </div>
        </div>
      </header>

      {/* Toast alert */}
      {toastMessage && (
        <div className="bg-emerald-900 border-b-2 border-emerald-400 text-emerald-100 text-xs sm:text-sm px-4 py-2 font-bold flex items-center justify-between animate-in fade-in duration-200 shrink-0">
          <span>{toastMessage}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="text-emerald-300 hover:text-white ml-2 text-xs uppercase"
          >
            ✕
          </button>
        </div>
      )}

      {/* Content scroll area */}
      <main className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-4 max-w-5xl xl:max-w-6xl w-full mx-auto">
        {/* Title Screen Artwork Customization Section (Admin Only) */}
        <section className="bg-slate-900/90 border-2 border-amber-500/60 rounded-xl p-3.5 sm:p-4 shadow-[0_0_20px_rgba(245,158,11,0.15)] space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-amber-400">
              <span className="text-xl">🖼️</span>
              <h2 className="text-sm sm:text-base font-black uppercase tracking-wider text-amber-300 flex items-center gap-2">
                <span>Artwork de l'Écran Titre</span>
                <span className="text-[10px] bg-amber-950 text-amber-300 border border-amber-500 px-2 py-0.5 rounded font-bold uppercase tracking-normal">
                  Admin
                </span>
              </h2>
            </div>

            <div className="flex items-center gap-2">
              {serverArtworkUrl ? (
                <span className="text-[11px] bg-emerald-950 text-emerald-300 border border-emerald-500/60 px-2.5 py-1 rounded-full font-bold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>Artwork Actif</span>
                </span>
              ) : (
                <span className="text-[11px] bg-slate-800 text-slate-400 border border-slate-700 px-2.5 py-1 rounded-full font-bold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-slate-500" />
                  <span>Par Défaut</span>
                </span>
              )}
            </div>
          </div>

          {/* Hidden HTML File Input with native iOS Photo Library support */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/jpg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileSelect(file);
              }
              // Reset input value to allow selecting same file again if wanted
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />

          {/* Upload Dropzone / Action Button */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
            {/* Left: Action & File selection */}
            <div className="flex flex-col justify-center gap-2.5 p-3.5 bg-slate-950/80 border border-slate-700/80 rounded-lg">
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => {
                    playSound('click');
                    fileInputRef.current?.click();
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-lg shadow-[0_0_15px_rgba(245,158,11,0.35)] border-2 border-amber-300 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span className="text-base">📸</span>
                  <span>Choisir une image</span>
                </button>

                {serverArtworkUrl && (
                  <button
                    type="button"
                    onClick={handleDeleteArtwork}
                    disabled={isDeletingArtwork}
                    className="px-3 py-2.5 bg-red-950 hover:bg-red-900 text-red-300 border border-red-500/70 rounded-lg text-xs font-bold transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 shrink-0"
                    title="Supprimer l'artwork actuel"
                  >
                    <span>🗑️</span>
                    <span>{isDeletingArtwork ? 'Suppression...' : 'Supprimer'}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Right: Live Preview Box */}
            <div className="p-3 bg-slate-950/90 border border-slate-700/80 rounded-lg flex flex-col items-center justify-center relative overflow-hidden min-h-[110px]">
              {pendingArtworkDataUrl ? (
                <div className="w-full flex flex-col items-center gap-2">
                  <div className="relative w-full h-28 rounded-md overflow-hidden border border-amber-400/80 shadow-md">
                    <img
                      src={pendingArtworkDataUrl}
                      alt="Nouvel artwork sélectionné"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1.5 left-1.5 bg-amber-500 text-slate-950 text-[10px] font-black px-2 py-0.5 rounded shadow">
                      Aperçu
                    </div>
                  </div>

                  <div className="flex items-center gap-2 w-full pt-1">
                    <button
                      type="button"
                      onClick={() => setPendingArtworkDataUrl(null)}
                      className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded border border-slate-600 transition-all cursor-pointer"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveArtworkToServer}
                      disabled={isUploadingArtwork}
                      className="flex-1 px-3 py-2 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white text-xs font-black uppercase tracking-wider rounded border border-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.4)] transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      <span>💾</span>
                      <span>{isUploadingArtwork ? 'Enregistrement...' : 'Enregistrer'}</span>
                    </button>
                  </div>
                </div>
              ) : serverArtworkUrl ? (
                <div className="w-full flex flex-col items-center gap-2">
                  <div className="relative w-full h-28 rounded-md overflow-hidden border border-emerald-500/60 shadow-md">
                    <img
                      src={serverArtworkUrl}
                      alt="Artwork actuel"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1.5 left-1.5 bg-emerald-950/90 text-emerald-300 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-500/60 flex items-center gap-1">
                      <span>✓</span>
                      <span>En ligne</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center p-3 space-y-1 text-slate-500">
                  <span className="text-2xl block opacity-40">🖼️</span>
                  <div className="text-xs text-slate-400">Fond épuré par défaut</div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Server-Synced Word & Translation Overrides Section (Text modifications only) */}
        {(() => {
          const overridesMap = getWordOverridesMap();

          // Build map of base unmodified words from gameData
          const defaultWordsMap = new Map<string, { kab: string; fr: string; icon?: string }>();
          ALL_UNIQUE_WORDS.forEach((w) => {
            const origKab = (w.originalKab || w.kab || '').trim();
            const origFr = (w.originalFr || w.fr || '').trim();
            const origIcon = (w.originalIcon || w.icon || '').trim();
            if (origKab) {
              const lowerKey = origKab.toLowerCase();
              if (!defaultWordsMap.has(lowerKey)) {
                defaultWordsMap.set(lowerKey, {
                  kab: origKab,
                  fr: origFr,
                  icon: origIcon || undefined,
                });
              }
            }
          });

          const seenKeys = new Set<string>();
          const overrideKeys = Object.keys(overridesMap).filter((k) => {
            const lower = k.toLowerCase().trim();
            if (seenKeys.has(lower)) return false;
            seenKeys.add(lower);

            const item = overridesMap[k];
            if (!item) return false;

            // Only keep words modified TEXTUALLY (not vocal modifications which are in Audio block)
            const defaultWord = defaultWordsMap.get(lower) || defaultWordsMap.get(item.kab.toLowerCase().trim());
            if (defaultWord) {
              const isKabChanged = item.kab.trim() !== defaultWord.kab.trim();
              const isFrChanged = item.fr.trim() !== defaultWord.fr.trim();
              const isIconChanged = (item.icon?.trim() || '') !== (defaultWord.icon?.trim() || '');
              return isKabChanged || isFrChanged || isIconChanged;
            }

            return true;
          });

          return (
            <section className="bg-slate-900/90 border-2 border-amber-500/60 rounded-xl p-3.5 sm:p-4 shadow-[0_0_20px_rgba(245,158,11,0.15)] space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-amber-400">
                  <span className="text-xl">📚</span>
                  <h2 className="text-sm sm:text-base font-black uppercase tracking-wider text-amber-300">
                    Mots Modifiés Textuellement
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      playSound('click');
                      setIsAddWordModalOpen(true);
                    }}
                    className="px-2.5 py-1 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:to-yellow-300 text-slate-950 font-black text-xs uppercase tracking-wider rounded border border-amber-300 shadow active:translate-y-0.5 transition-all flex items-center gap-1 cursor-pointer"
                    title="Ajouter un nouveau mot au dictionnaire public"
                  >
                    <span>✨</span>
                    <span>Ajouter un mot public</span>
                  </button>

                  <span className="text-[10px] bg-amber-950 text-amber-300 border border-amber-500 px-2 py-0.5 rounded font-bold">
                    {overrideKeys.length} mot{overrideKeys.length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {overrideKeys.length > 0 ? (
                <div className="pt-2 border-t border-slate-800 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                    {overrideKeys.map((origKey) => {
                      const item = overridesMap[origKey];
                      return (
                        <div
                          key={origKey}
                          className="flex items-center justify-between gap-2 bg-slate-950/80 border border-amber-500/40 p-2 rounded-lg text-xs"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {item.icon && <span className="text-base shrink-0">{item.icon}</span>}
                            <div className="min-w-0">
                              <div className="font-black text-amber-300 truncate">
                                {item.kab}
                                {origKey !== item.kab && (
                                  <span className="text-[10px] text-slate-500 font-normal ml-1">
                                    (orig: {origKey})
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-emerald-300 italic truncate">
                                {item.fr}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              await resetWordOverride(origKey);
                              triggerToast(`✅ Mot "${origKey}" réinitialisé aux valeurs d'origine.`);
                            }}
                            className="px-2 py-1 bg-rose-950/80 hover:bg-rose-900 text-rose-300 border border-rose-700/60 rounded text-[10px] font-bold uppercase shrink-0 cursor-pointer"
                            title="Rétablir la valeur par défaut"
                          >
                            Rétablir
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-black/40 rounded-lg border border-slate-800 text-center text-xs text-slate-400">
                  <p>Aucune modification de texte personnalisée.</p>
                </div>
              )}
            </section>
          );
        })()}

        {/* Contes, Dialogues & SMS Content Studio */}
        <section className="bg-slate-900/90 border-2 border-amber-500/60 rounded-xl p-3.5 sm:p-4 shadow-[0_0_20px_rgba(245,158,11,0.15)] space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-amber-400">
              <span className="text-xl">📜</span>
              <h2 className="text-sm sm:text-base font-black uppercase tracking-wider text-amber-300">
                Studio Narrations & Scénarios (Contes, Dialogues, SMS)
              </h2>
            </div>
            <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-lg border border-slate-800">
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setContentStudioTab('contes');
                }}
                className={`px-2.5 py-1 rounded text-xs font-bold transition-all cursor-pointer ${
                  contentStudioTab === 'contes'
                    ? 'bg-amber-600 text-slate-950 font-black shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                📜 Contes ({LEVEL_DATA.filter((lvl) => lvl.scroll).length})
              </button>
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setContentStudioTab('dialogues');
                }}
                className={`px-2.5 py-1 rounded text-xs font-bold transition-all cursor-pointer ${
                  contentStudioTab === 'dialogues'
                    ? 'bg-cyan-600 text-slate-950 font-black shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                💬 Dialogues ({DAILY_DIALOGUES_2026.length})
              </button>
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setContentStudioTab('sms');
                }}
                className={`px-2.5 py-1 rounded text-xs font-bold transition-all cursor-pointer ${
                  contentStudioTab === 'sms'
                    ? 'bg-emerald-600 text-slate-950 font-black shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                📱 SMS ({SMS_DISCUSSIONS.length})
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-400">
            {contentStudioTab === 'contes' && 'Enregistrez et gérez les narrations audio pour chaque page des 5 contes traditionnels kabyles.'}
            {contentStudioTab === 'dialogues' && 'Enregistrez et gérez les narrations audio pour chaque réplique et page des 3 dialogues immersifs.'}
            {contentStudioTab === 'sms' && 'Consultez les 7 scénarios de messagerie interactive avec leurs contacts et dialogues bilingues.'}
          </p>

          {/* TAB 1: CONTES */}
          {contentStudioTab === 'contes' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 animate-in fade-in duration-150">
              {LEVEL_DATA.filter((lvl) => lvl.scroll).map((lvl) => {
                const scroll = lvl.scroll!;
                let recordedPages = 0;
                for (let i = 0; i < 20; i++) {
                  if (hasRecordedAudio(getTalePageAudioKey(scroll.title, i))) {
                    recordedPages++;
                  }
                }

                return (
                  <div
                    key={lvl.id}
                    className="bg-slate-950/80 border border-amber-500/30 rounded-xl p-3 flex flex-col justify-between gap-3 hover:border-amber-400/60 transition-all"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">
                          Sanctuaire de {lvl.name}
                        </span>
                        {recordedPages > 0 ? (
                          <span className="text-[10px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-500/60 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span>{recordedPages} page{recordedPages > 1 ? 's' : ''} enregistrée{recordedPages > 1 ? 's' : ''}</span>
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700 px-2 py-0.5 rounded-full">
                            Non enregistré
                          </span>
                        )}
                      </div>
                      <h3 className="text-xs sm:text-sm font-bold text-white leading-snug">
                        {scroll.title}
                      </h3>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        setReadingTaleScroll({
                          title: scroll.title,
                          content: scroll.content,
                          sanctuaryName: lvl.name,
                        });
                      }}
                      className="w-full py-2 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-lg shadow-sm border border-amber-300 flex items-center justify-center gap-1.5 cursor-pointer active:scale-98 transition-all"
                    >
                      <span>🎙️</span>
                      <span>Ouvrir & Enregistrer le Conte</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* TAB 2: DIALOGUES 2026 */}
          {contentStudioTab === 'dialogues' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 animate-in fade-in duration-150">
              {DAILY_DIALOGUES_2026.map((dialogue) => {
                let recordedPages = 0;
                for (let i = 0; i < 20; i++) {
                  if (hasRecordedAudio(getTalePageAudioKey(dialogue.scroll.title, i))) {
                    recordedPages++;
                  }
                }

                return (
                  <div
                    key={dialogue.id}
                    className="bg-slate-950/80 border border-cyan-500/30 rounded-xl p-3 flex flex-col justify-between gap-3 hover:border-cyan-400/60 transition-all"
                  >
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-1">
                          <span>📍</span>
                          <span>{dialogue.location}</span>
                        </span>
                        {recordedPages > 0 ? (
                          <span className="text-[10px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-500/60 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span>{recordedPages} réplique{recordedPages > 1 ? 's' : ''} audio</span>
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700 px-2 py-0.5 rounded-full">
                            Non enregistré
                          </span>
                        )}
                      </div>
                      <h3 className="text-xs sm:text-sm font-bold text-white leading-snug">
                        {dialogue.title}
                      </h3>
                      <p className="text-[11px] text-slate-400 line-clamp-2 leading-relaxed">
                        {dialogue.desc}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        setReadingTaleScroll({
                          title: dialogue.scroll.title,
                          content: dialogue.scroll.content,
                          sanctuaryName: dialogue.location,
                        });
                      }}
                      className="w-full py-2 bg-gradient-to-r from-cyan-600 via-teal-500 to-cyan-600 hover:from-cyan-500 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-lg shadow-sm border border-cyan-300 flex items-center justify-center gap-1.5 cursor-pointer active:scale-98 transition-all"
                    >
                      <span>🎙️</span>
                      <span>Ouvrir & Enregistrer le Dialogue</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* TAB 3: SMS DISCUSSIONS */}
          {contentStudioTab === 'sms' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-1 animate-in fade-in duration-150">
              {SMS_DISCUSSIONS.map((sms) => (
                <div
                  key={sms.id}
                  className="bg-slate-950/80 border border-emerald-500/30 rounded-xl p-3 flex flex-col justify-between gap-2.5 hover:border-emerald-400/60 transition-all"
                >
                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-600/40 px-2 py-0.5 rounded-full">
                        {sms.themeTag}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {sms.messages.length} messages
                      </span>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xl">{sms.avatar}</span>
                      <div>
                        <h3 className="text-xs sm:text-sm font-bold text-white leading-tight">
                          {sms.contactName}
                        </h3>
                        <p className="text-[11px] text-slate-400">{sms.contactRole}</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-300 font-semibold pt-1">
                      {sms.title}
                    </p>
                    <p className="text-[10px] text-slate-400 italic line-clamp-2">
                      "{sms.contextDesc}"
                    </p>
                  </div>

                  <div className="text-[10px] bg-black/60 border border-slate-800 rounded p-2 text-slate-300 font-mono space-y-1">
                    <div className="text-emerald-400 font-bold">Aperçu du 1er message :</div>
                    <div className="line-clamp-1">Kabyle: {sms.messages[0]?.kab}</div>
                    <div className="line-clamp-1 text-slate-400">Français: {sms.messages[0]?.fr}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Firebase Cloud Realtime Sync & Archive Section */}
        <section className="bg-gradient-to-r from-emerald-950/80 via-slate-900 to-cyan-950/80 border-2 border-emerald-400 rounded-xl p-3.5 sm:p-4 shadow-[0_0_25px_rgba(16,185,129,0.25)] space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-emerald-300">
              <span className="text-xl">☁️</span>
              <h2 className="text-sm sm:text-base font-black uppercase tracking-wider text-emerald-200 flex items-center gap-2">
                <span>Base de Données Cloud & Sauvegarde</span>
                <span className="text-[10px] bg-emerald-900 text-emerald-100 border border-emerald-400 px-2 py-0.5 rounded font-bold uppercase tracking-normal">
                  Auto
                </span>
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-black/60 border border-emerald-500/40 text-emerald-300 font-mono">
                {isPreEnv ? 'ais-pre' : 'ais-dev'}
              </span>
            </div>
          </div>

          {/* Badges récapitulatifs du contenu garanti & exportable */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-0.5">
            <div className="bg-black/50 border border-amber-500/40 rounded-lg p-2 text-center">
              <div className="text-lg">📜</div>
              <div className="text-xs font-black text-amber-300">5 Contes</div>
              <div className="text-[10px] text-slate-400">Dans le backup JSON</div>
            </div>
            <div className="bg-black/50 border border-cyan-500/40 rounded-lg p-2 text-center">
              <div className="text-lg">💬</div>
              <div className="text-xs font-black text-cyan-300">3 Dialogues</div>
              <div className="text-[10px] text-slate-400">Dans le backup JSON</div>
            </div>
            <div className="bg-black/50 border border-emerald-500/40 rounded-lg p-2 text-center">
              <div className="text-lg">📱</div>
              <div className="text-xs font-black text-emerald-300">7 SMS Discussions</div>
              <div className="text-[10px] text-slate-400">Dans le backup JSON</div>
            </div>
            <div className="bg-black/50 border border-purple-500/40 rounded-lg p-2 text-center">
              <div className="text-lg">🎙️</div>
              <div className="text-xs font-black text-purple-300">Voix & Audios</div>
              <div className="text-[10px] text-slate-400">Firestore + Backup</div>
            </div>
          </div>

          <div className="pt-1">
            <button
              onClick={handleCloudSyncNow}
              disabled={isSyncingCloud}
              className="w-full sm:w-auto py-2.5 px-5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white border-2 border-emerald-300 rounded-lg font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-[0_0_15px_rgba(16,185,129,0.3)] active:translate-y-0.5 transition-all disabled:opacity-50"
            >
              <span>{isSyncingCloud ? '⏳' : '🔄'}</span>
              <span>{isSyncingCloud ? 'Synchronisation...' : 'Synchroniser Maintenant'}</span>
            </button>
          </div>

          {/* Sauvegarde Fichier .json (Optionnelle / Archive) */}
          <div className="p-3 bg-black/40 rounded-lg border border-slate-800 space-y-2">
            <div className="flex items-center gap-2 text-slate-300">
              <span className="text-sm">💾</span>
              <span className="text-xs font-bold uppercase tracking-wider">
                Export & Import Fichier JSON Complet (Contes, Dialogues, SMS, Vocaux, Mots)
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-0.5">
              <button
                onClick={handleExportBackup}
                className="py-2.5 px-3 bg-cyan-950 hover:bg-cyan-900 text-cyan-200 border border-cyan-500/80 rounded-lg font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer transition-all shadow-sm active:scale-98"
              >
                <span>💾</span>
                <span>Télécharger Sauvegarde (.json)</span>
              </button>

              <button
                onClick={() => {
                  playSound('click');
                  backupFileInputRef.current?.click();
                }}
                className="py-2.5 px-3 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-600 rounded-lg font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer transition-all shadow-sm active:scale-98"
              >
                <span>📂</span>
                <span>Restaurer Fichier (.json)</span>
              </button>
              <input
                ref={backupFileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleImportFileChange}
              />
            </div>
          </div>

          {syncStatusMsg && (
            <div className="p-2.5 rounded-lg bg-emerald-950/80 border border-emerald-400 text-xs font-bold text-emerald-200 flex items-center justify-between gap-2 animate-in fade-in duration-200">
              <span>{syncStatusMsg}</span>
              <button
                onClick={() => setSyncStatusMsg(null)}
                className="text-slate-400 hover:text-white px-2 py-0.5 rounded bg-black/40 text-xs cursor-pointer"
              >
                ✕
              </button>
            </div>
          )}
        </section>

        {/* Global Reset Action Card */}
        <section className="bg-slate-900/90 border-2 border-red-500/60 rounded-xl p-3.5 sm:p-4 shadow-[0_0_20px_rgba(239,68,68,0.15)] space-y-3">
          <div className="flex items-center gap-2 text-red-400">
            <span className="text-xl">⚠️</span>
            <h2 className="text-sm sm:text-base font-black uppercase tracking-wider text-red-300">
              Gestion des Sauvegardes Joueurs
            </h2>
          </div>

          <div className="pt-1 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="text-xs text-slate-400 font-mono">
              Joueurs enregistrés : <strong className="text-cyan-400">{regularPlayers.length}</strong>
            </div>

            <button
              onClick={() => {
                playSound('click');
                setShowConfirmResetAll(true);
              }}
              disabled={regularPlayers.length === 0}
              className={`py-2 px-4 font-bold text-xs uppercase tracking-wider border-2 rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer ${
                regularPlayers.length === 0
                  ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                  : 'bg-red-950 hover:bg-red-900 text-red-200 border-red-500 shadow-[2px_2px_0_#b91c1c] active:shadow-none active:translate-y-0.5'
              }`}
            >
              <span>🧹</span>
              <span>Réinitialiser les sauvegardes joueurs</span>
            </button>
          </div>
        </section>

        {/* Players List Table */}
        <section className="bg-slate-900/80 border border-slate-700 rounded-xl overflow-hidden shadow-md">
          <div className="p-3.5 bg-slate-800/80 border-b border-slate-700 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span>💾</span>
              <h3 className="text-xs sm:text-sm font-bold uppercase text-slate-200">
                Sauvegardes par Joueur ({profilesList.length})
              </h3>
            </div>
          </div>

          <div className="divide-y divide-slate-800 overflow-x-auto">
            {profilesList.map((player) => {
              const isAdmin = player.id === ADMIN_PLAYER_ID || player.isAdmin;
              return (
                <div
                  key={player.id}
                  className={`p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors ${
                    isAdmin ? 'bg-amber-950/20' : 'hover:bg-slate-800/40'
                  }`}
                >
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-white flex items-center gap-1.5">
                        <span>👤</span>
                        <span>{player.name}</span>
                      </span>
                      <span className="text-[10px] text-slate-400 bg-black/50 px-2 py-0.5 rounded border border-slate-800 font-mono">
                        ID: {player.id}
                      </span>
                      <span className="text-[10px] bg-cyan-950 text-cyan-300 border border-cyan-700 px-1.5 py-0.5 rounded">
                          Joueur
                        </span>
                    </div>

                    <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                      <span>
                        Régions : <strong className="text-emerald-400">{player.unlockedLevels.length} / 5</strong>
                      </span>
                      <span>•</span>
                      <span>
                        Score : <strong className="text-amber-400">{player.score}</strong>
                      </span>
                      <span>•</span>
                      <span>
                        Dernière sauvegarde : <span className="text-slate-300">{formatDate(player.lastSaved)}</span>
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
                    <button
                          onClick={() => {
                            playSound('click');
                            setSelectedPlayerToReset(player);
                          }}
                          className="px-2.5 py-1.5 bg-amber-950/80 hover:bg-amber-900 text-amber-200 border border-amber-600 rounded text-xs font-bold flex items-center gap-1 cursor-pointer transition-all active:scale-95"
                          title="Remettre la progression de ce joueur à zéro"
                        >
                          <span>🔄</span>
                          <span>Réinitialiser</span>
                        </button>
                        <button
                          onClick={() => {
                            playSound('click');
                            setSelectedPlayerToDelete(player);
                          }}
                          className="px-2.5 py-1.5 bg-red-950/80 hover:bg-red-900 text-red-200 border border-red-600 rounded text-xs font-bold flex items-center gap-1 cursor-pointer transition-all active:scale-95"
                          title="Supprimer la sauvegarde de ce joueur"
                        >
                          <span>🗑️</span>
                          <span>Supprimer</span>
                        </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </main>

      {/* Confirmation Modal for Reset All */}
      {showConfirmResetAll && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border-2 border-red-500 rounded-xl p-5 max-w-md w-full shadow-[0_0_30px_rgba(239,68,68,0.4)] space-y-4 animate-in zoom-in-95 duration-150">
            <div className="flex items-center gap-2 text-red-400 text-lg font-black uppercase">
              <span>🧹</span>
              <span>Confirmation de Réinitialisation</span>
            </div>

            <p className="text-xs sm:text-sm text-slate-200 leading-relaxed">
              Voulez-vous vraiment réinitialiser les sauvegardes de <strong className="text-cyan-400">{regularPlayers.length} joueur(s)</strong> ?
            </p>

            

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => {
                  playSound('click');
                  setShowConfirmResetAll(false);
                }}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs uppercase rounded-lg border border-slate-600 cursor-pointer"
              >
                Annuler
              </button>
              <button
                onClick={handleResetAllRegularPlayers}
                className="px-4 py-2 bg-red-800 hover:bg-red-700 text-white font-bold text-xs uppercase tracking-wider rounded-lg border-2 border-red-500 shadow-[2px_2px_0_#991b1b] cursor-pointer"
              >
                Confirmer la réinitialisation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Reset Single Player */}
      {selectedPlayerToReset && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border-2 border-amber-500 rounded-xl p-5 max-w-md w-full shadow-lg space-y-4">
            <div className="flex items-center gap-2 text-amber-400 text-base font-bold uppercase">
              <span>🔄</span>
              <span>Réinitialiser le joueur</span>
            </div>

            <p className="text-xs sm:text-sm text-slate-200">
              Remettre à zéro la progression de <strong>{selectedPlayerToReset.name}</strong> (score, niveaux débloqués et skins) ?
            </p>

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => setSelectedPlayerToReset(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs uppercase rounded-lg border border-slate-600 cursor-pointer"
              >
                Annuler
              </button>
              <button
                onClick={() => handleResetSingle(selectedPlayerToReset)}
                className="px-4 py-2 bg-amber-800 hover:bg-amber-700 text-white font-bold text-xs uppercase rounded-lg border-2 border-amber-500 cursor-pointer"
              >
                Réinitialiser ce joueur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Delete Single Player */}
      {selectedPlayerToDelete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border-2 border-red-500 rounded-xl p-5 max-w-md w-full shadow-lg space-y-4">
            <div className="flex items-center gap-2 text-red-400 text-base font-bold uppercase">
              <span>🗑️</span>
              <span>Supprimer le profil joueur</span>
            </div>

            <p className="text-xs sm:text-sm text-slate-200">
              Supprimer définitivement la sauvegarde du joueur <strong>{selectedPlayerToDelete.name}</strong> ?
            </p>

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => setSelectedPlayerToDelete(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs uppercase rounded-lg border border-slate-600 cursor-pointer"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDeleteSingle(selectedPlayerToDelete)}
                className="px-4 py-2 bg-red-800 hover:bg-red-700 text-white font-bold text-xs uppercase rounded-lg border-2 border-red-500 cursor-pointer"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Add Public Word Modal */}
      <AddPublicWordModal
        isOpen={isAddWordModalOpen}
        onClose={() => setIsAddWordModalOpen(false)}
        onWordAdded={(word) => {
          triggerToast(`✅ Mot public "${word.kab}" ajouté avec succès !`);
        }}
        existingCategories={SORTED_CATEGORIES}
      />

      {/* Admin Tale Reader & Voice Narration Studio */}
      {readingTaleScroll && (
        <ScrollDualScreenReader
          scroll={readingTaleScroll}
          sanctuaryName={readingTaleScroll.sanctuaryName}
          onClose={() => setReadingTaleScroll(null)}
          isAdmin={true}
        />
      )}
    </div>
  );
});
